import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileSignature, FileText, Download, Upload, Send, PenLine, CheckCircle2,
  Clock, Lock, AlertTriangle, X, Plus, User as UserIcon, Filter, RefreshCw, Trash2, Sparkles, Inbox,
  Undo2, ExternalLink, LayoutGrid, List as ListIcon
} from "lucide-react";
import { STATUS_LABEL } from "./constants";

export function AnalyticsInbox({ analytics, templates, onOpen }) {
  const t = analytics.totals || {};
  const stalled = analytics.stalled || {};
  const recent = analytics.recent || [];
  const expiring = analytics.retention_expiring_30d || [];
  const tplByKey = Object.fromEntries((templates || []).map((x) => [x.key, x]));

  const tiles = [
    { key: "draft", label: "DRAFT", val: t.draft || 0, cls: "bg-zinc-100 border-zinc-900" },
    { key: "pending_employee", label: "AWAITING EMPLOYEE", val: t.pending_employee || 0, cls: "bg-amber-100 border-amber-700" },
    { key: "pending_hr", label: "AWAITING HR", val: t.pending_hr || 0, cls: "bg-sky-100 border-sky-700" },
    { key: "completed", label: "COMPLETED", val: t.completed || 0, cls: "bg-emerald-100 border-emerald-700" },
  ];

  return (
    <div className="brut-card bg-white p-3 space-y-3" data-testid="hr-analytics-inbox">
      <div className="flex items-center gap-2">
        <Inbox className="w-4 h-4" />
        <div className="label-mono">HR INBOX</div>
        <span className="text-[10px] font-mono text-zinc-500">
          — updated {analytics.generated_at ? new Date(analytics.generated_at).toLocaleTimeString() : ""}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {tiles.map((tile) => (
          <div key={tile.key} className={`brut-card p-3 ${tile.cls}`} data-testid={`inbox-tile-${tile.key}`}>
            <div className="label-mono">{tile.label}</div>
            <div className="text-2xl font-black font-mono mt-1">{tile.val}</div>
          </div>
        ))}
      </div>
      {(stalled.pending_employee_gt_7d > 0 || stalled.pending_hr_gt_7d > 0) && (
        <div className="brut-card bg-rose-50 border-rose-700 p-2 text-xs font-mono flex items-center gap-2" data-testid="inbox-stalled">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-700" />
          <span>
            <b>{stalled.pending_employee_gt_7d}</b> awaiting-employee &gt; 7d &nbsp;·&nbsp;
            <b>{stalled.pending_hr_gt_7d}</b> awaiting-HR &gt; 7d
          </span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="label-mono mb-1">RECENT</div>
          {recent.length === 0 && <div className="text-[11px] font-mono text-zinc-500">No documents yet.</div>}
          <ul className="text-xs space-y-1">
            {recent.slice(0, 6).map((r) => (
              <li key={r.id} className="flex items-center gap-2">
                <button
                  onClick={() => onOpen && onOpen(r.id)}
                  className="text-left underline hover:text-blue-700 font-mono truncate flex-1"
                  data-testid={`inbox-recent-${r.id}`}
                  title={`Open ${tplByKey[r.template_type]?.title || r.template_type}`}
                >
                  {tplByKey[r.template_type]?.title || r.template_type} · {r.employee_name || "—"}
                </button>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-full ${STATUS_LABEL[r.status]?.cls || "bg-zinc-100"}`}>
                  {STATUS_LABEL[r.status]?.label || r.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="label-mono mb-1">RETENTION EXPIRING (30D)</div>
          {expiring.length === 0 && <div className="text-[11px] font-mono text-zinc-500">No locks ending in the next 30 days.</div>}
          <ul className="text-xs space-y-1">
            {expiring.slice(0, 6).map((e) => (
              <li key={e.user_id} className="font-mono truncate">
                <b>{e.name}</b> · unlocks <span className="text-zinc-500">{e.retention_locked_until?.slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}


